import React, { type PropsWithChildren, useEffect, useState } from 'react';

import getISOWeek from 'date-fns/getISOWeek';
import getYear from 'date-fns/getYear';
import { IntercomProvider, useIntercom } from 'react-use-intercom';

import UFOSegment from '@atlaskit/react-ufo/segment';
import { checkThirdParty } from '@atlassian/browser-storage-controls';

import { UserStatus } from '../.././common/constants/types';
import { INTERCOM_APP_ID } from '../../common/constants/intercom';
import { is3pProductKey } from '../../common/constants/products';
import { ErrorBoundary } from '../error-boundary';
import { useAvailableProducts, useIntercomHmac } from '../store/bootstrap';
import { useFeatures, useUser } from '../store/bootstrap/accessors';

const generateSearchPageVisitedString = () => {
	const currentDate = new Date();
	const year = getYear(currentDate);
	const week = getISOWeek(currentDate);
	return `search_page_visited_${year}_week_${week}`;
};

export const IntercomBootstrap = ({ children }: PropsWithChildren<{}>): React.JSX.Element => {
	const { boot, shutdown, trackEvent } = useIntercom();

	const [intercomHmac] = useIntercomHmac();
	const [userWithStatus] = useUser();
	const [features] = useFeatures();
	const availableProducts = useAvailableProducts();

	const isUserUnauthenticated = userWithStatus.status === UserStatus.Unauthenticated;

	useEffect(() => {
		// Do not boot if sideBySide feature is enabled
		if (features?.sideBySide?.enabled) {
			return;
		}

		// Do not boot if user details are not available
		if (isUserUnauthenticated || !userWithStatus.name || !userWithStatus.email || !intercomHmac) {
			return;
		}

		// Do not boot if there are no products available
		if (availableProducts.length === 0) {
			return;
		}

		const thirdPartyProducts = availableProducts.filter((product) => is3pProductKey(product));

		boot({
			userId: userWithStatus.id ?? undefined,
			name: userWithStatus.name,
			email: userWithStatus.email,
			userHash: intercomHmac,
			customAttributes: {
				// Some intercom users were registered without an email and succeeding
				// bootstrap calls did not update their email.
				// This change is temporary to ensure all users have an attribute that
				// actually stores the email for them :fried_angry_lol_cry:
				atlassian_account_email: userWithStatus.email,
				connected_third_parties: thirdPartyProducts.join(','),
			},
		});

		// A new intercom event will be created each week of the year.
		// Example: search_page_visited_2024_week_29
		// Events: https://app.intercom.com/a/apps/kkvkd7ql/settings/data/people?tab=events
		const eventString = generateSearchPageVisitedString();
		trackEvent(eventString);
		trackEvent('search_page_visited');

		return () => {
			shutdown();
		};
	}, [
		availableProducts,
		boot,
		features?.sideBySide?.enabled,
		intercomHmac,
		shutdown,
		isUserUnauthenticated,
		userWithStatus.id,
		userWithStatus.name,
		userWithStatus.email,
		trackEvent,
	]);

	return <React.Fragment>{children}</React.Fragment>;
};

const Intercom = ({ children }: PropsWithChildren<{}>) => {
	const [intercomAllowed, setIntercomAllowed] = useState(false);

	useEffect(() => {
		checkThirdParty(
			'intercom',
			async () => setIntercomAllowed(true),
			async () => setIntercomAllowed(false),
		);
	}, []);

	if (!intercomAllowed) {
		return <>{children}</>;
	}

	return (
		<IntercomProvider appId={INTERCOM_APP_ID}>
			<IntercomBootstrap>{children}</IntercomBootstrap>
		</IntercomProvider>
	);
};

const IntercomWithErrorBoundary = ({ children }: PropsWithChildren<{}>): React.JSX.Element => {
	return (
		<ErrorBoundary fallback={children} section="SearchPageIntercom">
			<UFOSegment name="search-page-intercom">
				<Intercom>{children}</Intercom>
			</UFOSegment>
		</ErrorBoundary>
	);
};

export default IntercomWithErrorBoundary;
